import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { listResearchReports, searchResearchStocks } from "@/api/research";
import PipelineStatusPanel from "./components/PipelineStatusPanel";
import StockScreener from "./components/StockScreener";

function prefSummary(pref: string): string {
  if (!pref) return "";
  const m: Record<string, string> = {};
  pref.split(/[，,、]/).forEach(p => {
    const [k, ...v] = p.split(/[:：]/);
    if (k && v.length) m[k.trim()] = v.join(":").trim();
  });
  const parts: string[] = [];
  if (m["purpose"]) parts.push(m["purpose"]);
  if (m["direction"]) parts.push(m["direction"]);
  if (m["horizon"]) parts.push(m["horizon"].replace(/[（(].*[）)]/, ""));
  if (m["focus"]) parts.push(m["focus"]);
  if (m["risk"]) parts.push(m["risk"]);
  if (m["valuation"]) parts.push(m["valuation"].replace(/[（(].*[）)]/, ""));
  return parts.length ? parts.join(" · ") : "";
}
import { useSubscription } from "@/hooks/useSubscription";
import PaywallModal from "@/components/feature/PaywallModal";

export default function ResearchHome() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "screener" ? "screener" : "reports";
  const sub = useSubscription();
  const [showPaywall, setShowPaywall] = useState(false);
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced stock search
  const doSearch = (q: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q.trim() || q.trim().length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await searchResearchStocks(q.trim());
        setSuggestions(res.list || []);
        setShowSuggestions(res.list?.length > 0);
        setSelectedIdx(-1);
      } catch {
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const goGenerate = (code: string, name?: string) => {
    if (!sub.subscribed && (reports?.length || 0) >= 0) {
      setShowPaywall(true);
      return;
    }
    const q = name ? `?step=quiz&name=${encodeURIComponent(name)}` : "?step=quiz";
    navigate(`/research/generate/${code}${q}`);
  };

  const selectStock = (stock: any) => {
    setSearch(stock.code || stock.stockCode || "");
    setShowSuggestions(false);
    goGenerate(stock.code || stock.stockCode, stock.name || stock.stockName);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === "Enter" && search) {
        goGenerate(search);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter" && selectedIdx >= 0) {
      e.preventDefault();
      selectStock(suggestions[selectedIdx]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    } else if (e.key === "Enter" && search) {
      goGenerate(search);
    }
  };

  useEffect(() => {
    listResearchReports({ limit: 10 })
      .then((d) => setReports(d.list || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // ── Poll every 3s while any report is still "生成中" ──
  const pendingRef = useRef(false);
  useEffect(() => {
    pendingRef.current = reports.some((r: any) => r.status === "生成中");
  }, [reports]);
  useEffect(() => {
    const timer = setInterval(() => {
      if (!pendingRef.current) return;
      listResearchReports({ limit: 10 })
        .then((d) => setReports(d.list || []))
        .catch(() => {});
    }, 3_000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4">
      <div className="mb-4 flex rounded-xl bg-background-200 p-0.5">
        <button
          onClick={() => setSearchParams({ tab: "screener" })}
          className={`flex-1 rounded-[10px] py-2.5 text-sm font-semibold transition-all ${
            activeTab === "screener" ? "bg-white text-foreground-950 shadow-sm" : "text-foreground-500"
          }`}
        >
          行情筛选
        </button>
        <button
          onClick={() => setSearchParams({ tab: "reports" })}
          className={`flex-1 rounded-[10px] py-2.5 text-sm font-semibold transition-all ${
            activeTab === "reports" ? "bg-white text-foreground-950 shadow-sm" : "text-foreground-500"
          }`}
        >
          我的研报
        </button>
      </div>

      {activeTab === "screener" ? <StockScreener /> : <>
      <h1 className="text-lg font-bold text-foreground-950 mb-4">研报工作台</h1>
      <div className="bg-white rounded-xl p-4 border border-background-200/60 mb-4">
        <div className="flex gap-2 mb-3 relative" ref={searchRef}>
          <div className="flex-1 relative">
            <input value={search}
              onChange={(e) => { setSearch(e.target.value); doSearch(e.target.value); }}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              placeholder="输入股票代码或名称，如 300750"
              className="w-full bg-background-100 rounded-xl px-4 py-2.5 text-sm text-foreground-950 placeholder:text-foreground-300 outline-none border-none" />
            {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl border border-background-200/60 shadow-lg z-50 max-h-[240px] overflow-y-auto">
                {suggestions.map((s, i) => (
                  <button key={s.code || s.stockCode || i}
                    onMouseDown={() => selectStock(s)}
                    onMouseEnter={() => setSelectedIdx(i)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left cursor-pointer transition-colors ${
                      i === selectedIdx ? "bg-primary-50" : "hover:bg-background-50"
                    } ${i === 0 ? "rounded-t-xl" : ""} ${i === suggestions.length - 1 ? "rounded-b-xl" : ""}`}>
                    <span className="text-sm font-semibold text-foreground-950">{s.name || s.stockName}</span>
                    <span className="text-[10px] text-foreground-400 font-mono">{s.code || s.stockCode}</span>
                    {s.industry && <span className="text-[9px] text-foreground-300 ml-auto">{s.industry}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => search && goGenerate(search)}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-primary-500 to-accent-500 text-white cursor-pointer whitespace-nowrap">
            生成研报
          </button>
        </div>
        <p className="text-[10px] text-foreground-400">单份消耗 1 次额度 · 预计 3-5 分钟</p>
      </div>
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-foreground-950">最近研报</span>
        <button onClick={() => navigate("/research/library")} className="text-xs text-primary-500 cursor-pointer">查看全部 →</button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : reports.length === 0 ? (
        <div className="bg-white rounded-xl p-8 border border-background-200/60 text-center">
          <i className="ri-file-list-3-line text-3xl text-foreground-300" />
          <p className="text-sm text-foreground-500 mt-2">还没有研报</p>
          <p className="text-xs text-foreground-400 mt-1">输入股票代码开始你的第一份</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {reports.map((r: any) => (
            <div key={r.id} onClick={() => navigate(`/research/report/${r.id}`)}
              className="bg-white rounded-xl p-4 border border-background-200/60 cursor-pointer hover:border-primary-200 transition-colors">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-foreground-950">{r.stockName}<span className="text-[10px] text-foreground-400 font-normal ml-2">{r.stockCode}</span></span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                  style={{ color: r.rating === "积极关注" || r.rating === "强烈推荐" ? "#E11D48" : r.rating === "审慎评估" ? "#10B981" : "#64748B", background: "rgba(225,29,72,0.1)" }}>{r.rating || "--"}</span>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-foreground-400 mb-2">
                <span>{r.industry || "--"}</span><span>·</span><span>{r.createdAt ? new Date(r.createdAt * 1000).toLocaleDateString() : ""}</span>
              {r.preference && prefSummary(r.preference) && <div className="text-[10px] text-foreground-500 leading-relaxed">{prefSummary(r.preference)}</div>}
              </div>
              {r.targetPrice && <div className="text-[11px]"><span className="text-foreground-400">目标价 </span><span className="font-semibold text-foreground-950">¥{r.targetPrice}</span></div>}
            </div>
          ))}
        </div>
      )}
      <PipelineStatusPanel />
      </>}
      <PaywallModal open={showPaywall} onClose={() => setShowPaywall(false)} description="AI 研报功能需要联系客服开通" />
    </div>
  );
}
